import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {get,push,set,ref} from 'firebase/database';
import { useAuth,database,storage,userProfile} from '@/contexts/AuthContext';
import { ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Upload, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const techStackOptions = [
  'React', 'Angular', 'Vue', 'Node.js', 'Python', 'Java', 'C++', 'JavaScript',
  'TypeScript', 'Django', 'Flask', 'Spring Boot', 'MongoDB', 'PostgreSQL',
  'MySQL', 'Firebase', 'AWS', 'Docker', 'Kubernetes', 'TensorFlow', 'PyTorch'
];

export default function UploadProjectPage() {
  const { authToken,userProfile } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [githubLink, setGithubLink] = useState('');
  const [status, setStatus] = useState('');
  const [allowCollaboration, setAllowCollaboration] = useState(false);
  const [selectedTechStack, setSelectedTechStack] = useState([]);
  const [file, setFile] = useState(null);

  const handleTechStackToggle = (tech) => {
    setSelectedTechStack(prev =>
      prev.includes(tech)
        ? prev.filter(t => t !== tech)
        : [...prev, tech]
    );
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.size > 2 * 1024 * 1024) {
        toast.error('File size should be less than 2MB');
        return;
      }
      setFile(selectedFile);
    }
  };

  const handleSubmit = async (e) => {
  e.preventDefault();

  if (selectedTechStack.length === 0) {
    toast.error("Please select at least one technology");
    return;
  }

  setLoading(true);

  try {
    // 1️⃣ Create project data
    const projectData = {
      uid: userProfile.uid,
      user_name: userProfile.full_name,
      title,
      description,
      github_link: githubLink,
      status,
      allow_collaboration: allowCollaboration,
      tech_stack: selectedTechStack,
      created_at: new Date().toISOString().split("T")[0],
      file_url: null, // default
    };

    // 2️⃣ Generate Firebase Realtime Database ID
    const newRef = push(ref(database, "projects"));
    const generatedId = newRef.key;

    // 3️⃣ If file exists, upload to Firebase Storage
    if (file) {
      const filePath = `project_files/${generatedId}/${file.name}`;
      const fileRef = storageRef(storage, filePath);

      // Upload file
      await uploadBytes(fileRef, file);

      // Get file URL
      const downloadURL = await getDownloadURL(fileRef);

      // Add to projectData
      projectData.file_url = downloadURL;
    }

    // 4️⃣ Save project along with file URL
    await set(newRef, {
      id: generatedId,
      college_name: userProfile.college_name,
      ...projectData
    });

    toast.success("Project uploaded successfully");
    
    // Optional redirect
    navigate(`/project/${generatedId}`);

  } catch (error) {
    console.error("Firebase Error:", error);
    toast.error("Failed to upload project");
  }

  setLoading(false);
};


  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-cyan-50 to-blue-50" data-testid="upload-project-page">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold gradient-text mb-2">Upload Your Project</h1>
          <p className="text-gray-600">Share your amazing work with the community</p>
        </div>

        <Card className="glass border-white/50">
          <CardHeader>
            <CardTitle>Project Details</CardTitle>
            <CardDescription>Fill in the information about your project</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="title">Project Title *</Label>
                <Input
                  id="title"
                  placeholder="E-Commerce Platform"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  required
                  data-testid="project-title-input"
                />
              </div>

              <div>
                <Label>Tech Stack * (Select all that apply)</Label>
                <div className="flex flex-wrap gap-2 mt-2 p-4 border rounded-lg bg-white/50">
                  {techStackOptions.map(tech => (
                    <Badge
                      key={tech}
                      variant={selectedTechStack.includes(tech) ? 'default' : 'outline'}
                      className={`cursor-pointer tech-badge ${
                        selectedTechStack.includes(tech)
                          ? 'bg-gradient-to-r from-sky-500 to-cyan-500 text-white'
                          : ''
                      }`}
                      onClick={() => handleTechStackToggle(tech)}
                      data-testid={`tech-badge-${tech}`}
                    >
                      {tech}
                      {selectedTechStack.includes(tech) && <X className="w-3 h-3 ml-1" />}
                    </Badge>
                  ))}
                </div>
              </div>

              <div>
                <Label htmlFor="description">Project Description *</Label>
                <Textarea
                  id="description"
                  placeholder="Describe your project in detail..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={6}
                  required
                  data-testid="project-description-input"
                />
              </div>

              <div>
                <Label htmlFor="github">GitHub Repository Link</Label>
                <Input
                  id="github"
                  type="url"
                  placeholder="https://github.com/username/repo"
                  value={githubLink}
                  onChange={(e) => setGithubLink(e.target.value)}
                  data-testid="github-link-input"
                />
              </div>

              <div>
                <Label htmlFor="status">Project Status *</Label>
                <Select value={status} onValueChange={setStatus} required>
                  <SelectTrigger data-testid="status-select">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Idea Stage">Idea Stage</SelectItem>
                    <SelectItem value="In Development">In Development</SelectItem>
                    <SelectItem value="Completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="file">Upload Project Details File (Optional)</Label>
                <p className="text-sm text-gray-500 mb-2">PDF, DOCX, or TXT (Max 2MB)</p>
                <Input
                  id="file"
                  type="file"
                  accept=".pdf,.docx,.txt"
                  onChange={handleFileChange}
                  data-testid="file-input"
                />
                {file && (
                  <div className="mt-2 flex items-center gap-2">
                    <Upload className="w-4 h-4 text-sky-500" />
                    <span className="text-sm text-gray-600">{file.name}</span>
                  </div>
                )}
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="collaboration"
                  checked={allowCollaboration}
                  onCheckedChange={setAllowCollaboration}
                  data-testid="collaboration-checkbox"
                />
                <Label htmlFor="collaboration" className="cursor-pointer">
                  I'm open to collaboration on this project
                </Label>
              </div>

              <div className="flex gap-4">
                <Button
                  type="submit"
                  className="flex-1 bg-gradient-to-r from-sky-500 to-cyan-500 hover:from-sky-600 hover:to-cyan-600"
                  disabled={loading}
                  data-testid="submit-project-button"
                >
                  {loading ? 'Uploading...' : 'Upload Project'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/feed')}
                  data-testid="cancel-button"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}